/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import React, { useMemo, useEffect, useState } from 'react';
import { SkeletonText, EmptyState, CARD_SIZES } from 'carbon-addons-iot-react';
import { defineMessages, useIntl } from 'react-intl';

import Iframe from '../../../Common/components/Iframe/Iframe';
import { ThirdPartyContentTypes } from '../../types/dashboardTypes';
import { getDimensionsForDeviceAPI } from '../../../Setup/services/HierarchiesAPI';

import { RESOURCE_TYPES } from 'dataConstants';
import { formatError, isValidHttpsURL, isValidHttpURL } from 'Utils';
import { ErrorType } from 'CommonTypes';
import { formatErrorResponse } from 'responseFormatterFunctions';

const OptimizedSkeletonText = React.memo(SkeletonText);

type ThirdPartyCardContainerProps = {
  hierarchyPath?: string;
  hierarchyType: string;
  data?: any;
  testId?: string;
  title?: string;
  size?: { height?: number; width?: number };
  cardSize: CARD_SIZES;
  id?: string;
  isPreview?: boolean; // is in editor mode
  customContent?: { type: ThirdPartyContentTypes; value?: string };
};

const defaultProps = {
  hierarchyPath: '',
  data: null,
  testId: 'third-party-card-content',
  title: null,
  size: { width: '100%', height: '100%' },
  id: null,
  isPreview: false,
  customContent: {},
};

const messages = defineMessages({
  invalidValue: 'Specify a valid URL.',
  invalidDimensionValue: `Specify a valid URL. The dimension ''{dimensionName}'' with the value ''{dimensionValue}'' is not a valid URL. Please provide a valid dimension that contains a valid URL.`,
  httpNotSupported: 'Specify a URL that uses HTTPS protocol.',
});

const ThirdPartyCardContainer = ({
  hierarchyPath,
  hierarchyType,
  data,
  testId,
  title,
  size,
  cardSize,
  id,
  isPreview,
  customContent,
}: ThirdPartyCardContainerProps): JSX.Element => {
  const { formatMessage } = useIntl();
  const { type, value } = customContent;
  const [isLoading, setIsLoading] = useState(false);
  const [srcURL, setSrcURL] = useState(type === ThirdPartyContentTypes.URL ? value : undefined);
  const [error, setError] = useState<string | ErrorType | void>();

  const cardErrorState = useMemo(
    () => (error ? <EmptyState icon="error" title={error} body="" /> : null),
    [error]
  );

  useEffect(() => {
    if (!(isNil(value) || value.trim().length === 0)) {
      setError();

      const validateAndSetDimensionValue = (dimensionName, dimensionValue): void => {
        const dimensionValueString = dimensionValue ? dimensionValue.toString() : '';

        if (isValidHttpsURL(dimensionValueString)) {
          setSrcURL(dimensionValueString);
        } else if (isValidHttpURL(dimensionValueString)) {
          setError(formatMessage(messages.httpNotSupported));
        } else {
          setError(
            formatMessage(messages.invalidDimensionValue, {
              dimensionName,
              dimensionValue: dimensionValueString,
            })
          );
        }
      };

      if (type === ThirdPartyContentTypes.DIMENSION) {
        if (isPreview && hierarchyType === RESOURCE_TYPES.DEVICE) {
          setIsLoading(true);
          getDimensionsForDeviceAPI(hierarchyPath)
            .then(dimensions => {
              if (!isEmpty(dimensions)) {
                validateAndSetDimensionValue(value, dimensions[0][value]);
              }
              setIsLoading(false);
            })
            .catch(e => {
              console.error(e);
              setError(
                formatError(e, formatMessage)?.message
                  ? formatError(e, formatMessage).message
                  : formatErrorResponse(e) || e
              );
              setIsLoading(false);
            });
        } else if (!isEmpty(data)) {
          validateAndSetDimensionValue(value, data[0][value]);
        }
      } else if (type === ThirdPartyContentTypes.URL) {
        if (isValidHttpsURL(value)) {
          setSrcURL(value);
        } else {
          setError(formatMessage(messages.invalidValue));
        }
      }
    }
  }, [isPreview, type, value, hierarchyPath, hierarchyType, data, formatMessage]);

  const iFrame = useMemo(
    () =>
      isNil(value) || value.trim().length === 0 || isNil(srcURL) ? null : (
        <Iframe id={id} src={srcURL} title={title} size={size} />
      ),
    [value, srcURL, id, title, size]
  );

  const isSmallOrThin = useMemo(() => {
    const isSmall =
      cardSize === CARD_SIZES.SMALL ||
      cardSize === CARD_SIZES.SMALLWIDE ||
      cardSize === CARD_SIZES.SMALLFULL;
    const isLargeThin = cardSize === CARD_SIZES.LARGETHIN;
    const isMediumThin = cardSize === CARD_SIZES.MEDIUMTHIN;
    return isSmall || isMediumThin || isLargeThin;
  }, [cardSize]);

  return (
    <div key={id} data-testid={`${testId}`} className="third-party-card-container">
      {isPreview && isLoading ? (
        <OptimizedSkeletonText
          data-testid="third-party-card-content-loading"
          paragraph
          lineCount={isSmallOrThin ? 2 : 3}
          width="100%"
        />
      ) : error ? (
        cardErrorState
      ) : (
        iFrame
      )}
    </div>
  );
};

ThirdPartyCardContainer.defaultProps = defaultProps;
export default ThirdPartyCardContainer;
